import React, { useState } from 'react';
import './TaxCalculator.css';

const TaxCalculator = () => {
    const [year, setYear] = useState('2024');
    const [income, setIncome] = useState('');
    const [cryptoGain, setCryptoGain] = useState('');
    const [derivativesGain, setDerivativesGain] = useState('');
    const [tax, setTax] = useState(null);
    const [postTaxIncome, setPostTaxIncome] = useState(null);
    const [totalIncome2, setTotalIncome2] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [heldLongerThanYear, setHeldLongerThanYear] = useState(false);
    const [activeInfo, setActiveInfo] = useState(null);

    const formatNumber = (value) => {
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const handleInputChange = (e, setValue) => {
        const inputValue = e.target.value.replace(/\./g, '');
        if (!isNaN(inputValue) && inputValue !== '') {
            const formattedValue = formatNumber(inputValue);
            setValue(formattedValue);
        } else {
            setValue('');
        }
        setTax(null);
        setPostTaxIncome(null);
    };

    const handleToggleChange = () => {
        setHeldLongerThanYear(!heldLongerThanYear);
        setTax(null);
        setPostTaxIncome(null);
    };

    const calculateTax = () => {
        if (income === '' && cryptoGain !== '') {
            setShowConfirm(true);
            return;
        }
        performCalculation();
    };

    const performCalculation = () => {
        const incomeValue = parseFloat(income.replace(/\./g, '')) || 0;
        let cryptoGainValue = parseFloat(cryptoGain.replace(/\./g, '')) || 0;
        const derivativesGainValue = parseFloat(derivativesGain.replace(/\./g, '')) || 0;

        let totalIncome = incomeValue + cryptoGainValue;
        const derivativesTax = derivativesGainValue * 0.25;

        let totalIncome2 = incomeValue + cryptoGainValue + derivativesGainValue;

        if (heldLongerThanYear || cryptoGainValue <= 600) {
            totalIncome = totalIncome - cryptoGainValue;
        }

        let taxAmount = 0;

        if (totalIncome <= 11604) {
            taxAmount = 0;
        } else if (totalIncome <= 18174) {
            taxAmount = (totalIncome - 11604) * 0.14;
        } else if (totalIncome <= 60000) {
            const progressionRate = (0.42 - 0.14) / (60000 - 18174);
            taxAmount = (18174 - 11604) * 0.14;
            taxAmount += (totalIncome - 18174) * (0.14 + progressionRate * (totalIncome - 18174));
        } else if (totalIncome <= 277826) {
            taxAmount = (18174 - 11604) * 0.14;
            taxAmount += (60000 - 18174) * 0.42;
            taxAmount += (totalIncome - 60000) * 0.42;
        } else {
            taxAmount = (18174 - 11604) * 0.14;
            taxAmount += (60000 - 18174) * 0.42;
            taxAmount += (277826 - 60000) * 0.42;
            taxAmount += (totalIncome - 277826) * 0.45;
        }

        taxAmount += derivativesTax;
        const postTaxIncomeValue = (incomeValue + cryptoGainValue + derivativesGainValue) - taxAmount;

        setTax(Math.round(taxAmount));
        setPostTaxIncome(Math.round(postTaxIncomeValue));
        setTotalIncome2(Math.round(totalIncome2));
        setShowConfirm(false);
    };

    const handleConfirm = () => {
        setShowConfirm(false);
        performCalculation();
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    const resetCalculator = () => {
        setYear('2024');
        setIncome('');
        setCryptoGain('');
        setDerivativesGain('');
        setTax(null);
        setPostTaxIncome(null);
        setHeldLongerThanYear(false);
    };

    const handleInfoClick = (infoType, e) => {
        e.stopPropagation();
        e.preventDefault();
        setActiveInfo(activeInfo === infoType ? null : infoType);
    };

    return (
        <div className="tax-calculator">
            <h1 className="spruch">Steuer einfach berechnen</h1>
            
            <div className="input-group">
                <label>
                    <span className="dropDown">Jahr</span>
                    <select value={year} onChange={(e) => setYear(e.target.value)}>
                        <option value="2024">2024</option>
                        <option value="2023">2023</option>
                    </select>
                </label>
            </div>

            <div className="input-group">
                <label>
                    <span>Brutto-Einkommen</span>
                    <div className={`info ${activeInfo === 'income' ? 'active' : ''}`} onClick={(e) => handleInfoClick('income', e)}>
                        <span className="info-icon">i</span>
                        <div className="info-text">
                            <p>Trage hier dein Jahreseinkommen vor Steuern ein. Dieser Wert ist sehr wichtig für die Berechnung, da Gewinne aus Handel mit Spot-Kryptowährungen mit der Einkommenssteuer besteuert werden.</p>
                        </div>
                    </div>
                    <div className="input-wrapper">
                        <input
                            type="text"
                            value={income}
                            onChange={(e) => handleInputChange(e, setIncome)}
                            placeholder="z.B. 50.000"
                        />
                        <div className="currency">EUR</div>
                    </div>
                </label>
            </div>

            <div className="input-group">
                <label>
                    <span>Kryptogewinn</span>
                    <div className={`info ${activeInfo === 'crypto' ? 'active' : ''}`} onClick={(e) => handleInfoClick('crypto', e)}>
                        <span className="info-icon">i</span>
                        <div className="info-text">
                            <p>Beim normalen Handel mit Kryptowährungen hälst du den Coin selbst in deiner Wallet (Spot).</p>
                            <p>Gewinn = (Verkaufspreis - Einkaufspreis) * Menge</p>
                            <p>Wichtig: Wenn du deine Spot-Kryptowährungen länger als 1 Jahr hälst, fallen keine Steuern an. Markiere unbedingt den Toggle unter dem Eingabfeld.</p>
                        </div>
                    </div>
                    <div className="input-wrapper">
                        <input
                            type="text"
                            value={cryptoGain}
                            onChange={(e) => handleInputChange(e, setCryptoGain)}
                            placeholder="z.B. 10.000"
                        />
                        <div className="currency">EUR</div>
                    </div>
                </label>
            </div>

            <div className="input-group">
                <label className="toggle-label">
                    Haltedauer länger als 1 Jahr?
                    <div className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={heldLongerThanYear}
                            onChange={handleToggleChange}
                        />
                        <span className="slider"></span>
                    </div>
                </label>
            </div>

            <div className="input-group">
                <label>
                    <span>Gewinn durch Krypto-Derivate</span>
                    <div className={`info ${activeInfo === 'derivatives' ? 'active' : ''}`} onClick={(e) => handleInfoClick('derivatives', e)}>
                        <span className="info-icon">i</span>
                        <div className="info-text">
                            <p>Im Gegensatz zum Spot-Handel gibt es auch die Möglichkeit mit Derivaten (Zertifikaten) auf Kryptowährungen zu handeln. Dieser Fall wird anders besteuert und die Haltedauer von 1 Jahr gibt es hier nicht.</p>
                        </div>
                    </div>
                    <div className="input-wrapper">
                        <input
                            type="text"
                            value={derivativesGain}
                            onChange={(e) => handleInputChange(e, setDerivativesGain)}
                            placeholder="z.B. 10.000"
                        />
                        <div className="currency">EUR</div>
                    </div>
                </label>
            </div>

            <div className="buttons">
                <button onClick={calculateTax}>Berechne Steuer</button>
                {tax !== null && <button className="secondary" onClick={resetCalculator}>Zurücksetzen</button>}
            </div>

            {tax !== null && (
                <div className="tax-result">
                    <h3>Ergebnis</h3>
                    <p>Gesamteinkommen: <span className="highlight">{totalIncome2.toLocaleString()} EUR</span></p>
                    <p>Steuerlast: <span className="highlight tax-amount">{tax.toLocaleString()} EUR</span></p>
                    <p>Nettoeinkommen: <span className="highlight net-income">{postTaxIncome.toLocaleString()} EUR</span></p>
                </div>
            )}

            {showConfirm && (
                <div className="confirm-dialog">
                    <p>Möchten Sie die Berechnung ohne Brutto-Einkommen durchführen?</p>
                    <div className="confirm-buttons">
                        <button onClick={handleConfirm}>Ja</button>
                        <button onClick={handleCancel}>Nein</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaxCalculator;